import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { Modal } from "@/components/ui/Modal";
import type { ChaoMapaMedicaoTab } from "@/services/chaoDeObra";
import {
  atestarChaoMedicao,
  createChaoMedicao,
  patchChaoMedicao,
} from "@/services/chaoDeObra";
import { formatMedicaoRange } from "./chaoAvancoMapaStatus";

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  unitIds: string[];
  medicoes: ChaoMapaMedicaoTab[];
  canAtestar: boolean;
  onChanged: () => void;
}

export function ChaoAvancoMapaMedicoesModal({
  open,
  onClose,
  costCenterId,
  unitIds,
  medicoes,
  canAtestar,
  onChanged,
}: Props) {
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await createChaoMedicao({ costCenterId, dataInicio: inicio, dataFim: fim });
      if (!res.success) {
        setError(res.error ?? "Não foi possível criar a medição.");
        return;
      }
      setInicio("");
      setFim("");
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Medições da obra" size="lg" closeOnEscape>
      <div className="flex flex-col gap-4 p-5">
        <p className="text-sm text-slate-600">
          Cada medição (M1, M2…) é o recorte de previsão/atesto da obra. O avanço das casas continua
          sendo o percentual já lançado por serviço.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <DateInput label="Início" value={inicio} onChange={setInicio} />
          <DateInput label="Fim" value={fim} onChange={setFim} />
          <Button onClick={() => void create()} loading={busy} disabled={!inicio || !fim || busy}>
            Nova medição
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Medição</th>
                <th className="text-left px-3 py-2">Período</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {medicoes.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                    Nenhuma medição cadastrada.
                  </td>
                </tr>
              )}
              {medicoes.map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">M{m.numero}</td>
                  <td className="px-3 py-2">{formatMedicaoRange(m.dataInicio, m.dataFim)}</td>
                  <td className="px-3 py-2">{m.status}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    {m.status === "ABERTA" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          void patchChaoMedicao(m.id, { status: "FECHADA" }).then(onChanged);
                        }}
                      >
                        Fechar
                      </Button>
                    )}
                    {m.status !== "ATESTADA" && canAtestar && (
                      <Button
                        size="sm"
                        onClick={() => {
                          void atestarChaoMedicao(m.id, unitIds).then(onChanged);
                        }}
                      >
                        Atestar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
